import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * P3B.5 — OAuth provider buttons for the login + signup pages.
 *
 * Behaviour:
 *   - Fetches `/auth/oauth/providers` on mount. The server returns the
 *     enabled providers (filtered by env-var presence). When the list
 *     is empty, the component renders nothing — login/signup pages
 *     stay password-only.
 *   - Each enabled provider renders as a top-level `<a>` to
 *     `/auth/oauth/<kind>/start`. The endpoint sets a state cookie and
 *     302s out to the provider's consent screen, so a real navigation
 *     (not `fetch`) is required.
 *   - Reads `?oauth=<status>` query param; when present, shows a
 *     dismissible inline message describing the outcome of the previous
 *     OAuth round-trip (cancelled / email already registered / error).
 */
interface ProviderInfo {
  kind: string;
  displayName: string;
}

type OAuthStatus =
  | 'cancelled'
  | 'email_taken'
  | 'corp_name_taken'
  | 'error'
  | null;

export function OAuthProviders() {
  const [providers, setProviders] = useState<ProviderInfo[] | null>(null);
  const [params] = useSearchParams();
  const status = (params.get('oauth') as OAuthStatus) ?? null;

  useEffect(() => {
    let cancelled = false;
    fetch('/auth/oauth/providers', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { providers: [] }))
      .then((data: { providers: ProviderInfo[] }) => {
        if (!cancelled) setProviders(data.providers ?? []);
      })
      .catch(() => {
        if (!cancelled) setProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Don't reserve space until the fetch resolves — keeps the page from
  // jumping when the OAuth section pops in.
  if (providers === null) return null;
  if (providers.length === 0 && status === null) return null;

  return (
    <div className="space-y-3 border-t border-line px-6 py-4">
      {status && <OAuthStatusBanner status={status} />}
      {providers.length > 0 && (
        <>
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
            — or sign in with —
          </p>
          <div className="space-y-2">
            {providers.map((p) => (
              <a
                key={p.kind}
                href={`/auth/oauth/${p.kind}/start`}
                className="block w-full rounded-md border border-line bg-panel-2 px-4 py-2.5 text-center text-[12px] font-bold tracking-wide text-fg transition-colors hover:border-line-strong hover:bg-panel-3"
              >
                Continue with {p.displayName}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function OAuthStatusBanner({ status }: { status: NonNullable<OAuthStatus> }) {
  const message =
    status === 'cancelled'
      ? 'OAuth sign-in was cancelled.'
      : status === 'email_taken'
        ? 'An account with that email already exists. Sign in with your password first; you can link OAuth from settings later.'
        : status === 'corp_name_taken'
          ? 'Your OAuth login conflicts with too many existing corp names. Try password signup or contact support.'
          : 'OAuth sign-in failed. Please try again.';
  return (
    <div className="rounded-md border border-accent/40 bg-accent/10 px-3 py-2 text-[12px] font-semibold leading-snug text-accent">
      {message}
    </div>
  );
}
